import { Form } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { ConfirmDeletingElement } from '@app/components';
import { TaskModal } from '@app/components/Modals/TaskModal';
import {
  emptyFn,
  FRONT_WARNING,
  SUCCESS,
  TASK_LIST_PATH,
} from '@app/constants';
import { useTasksAccess, useUserPrint } from '@app/hooks';
import { FinishTaskModal, HistoryModal } from '@app/pages/TasksList/components';
import {
  DELETE_TASKS_KEY,
  TASK_HISTORY_KEY,
} from '@app/pages/TasksList/constants/dropdownKeys';
import { ModalDropdownItems } from '@app/pages/TasksList/data/ModalDropdownItems';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  useChangeEmployeeModal,
  useDeleteTasksModal,
  useFinishTaskModal,
  useTaskListRouting,
  useTaskModal,
  useTaskRegistry,
  useTasksCalendar,
  useHistoryModal,
} from '@app/providers/TaskListProvider/hooks';
import { TaskListProviderContext } from '@app/providers/TaskListProvider/TaskListProviderContext';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';
import {
  useDeleteTaskMutation,
  useDeleteTasksListMutation,
} from '@app/services/rtkQuery/CrmTasks/api';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import { getMassActionMenuItem } from '@app/utils/getMassActionMenuItem';

import type { ItemType } from 'antd/es/menu/interface';
import type { BaseOptionType } from 'antd/es/select';

const { Provider } = TaskListProviderContext;

export const TaskListProvider: NTaskListProvider.Main = ({ children }) => {
  const onSuccessRef = useRef<() => void>(emptyFn);

  const onChangeSuccess = useCallback((callback: () => void) => {
    onSuccessRef.current = callback;
  }, []);
  const [currentTask, setCurrentTask] = useState<NCrmTasks.ModelCrmTask | null>(
    null
  );
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);
  const { pathname } = useLocation();
  const isTaskList = useMemo(() => pathname === TASK_LIST_PATH, [pathname]);

  const { activeBranchId: ActiveBranchId, userId } = useUserPrint();
  const { showNotification } = useNotification();
  const [deleteTask] = useDeleteTaskMutation();
  const [deleteTaskList] = useDeleteTasksListMutation();

  const selectTaskForAction = useCallback(
    (target: number | NCrmTasks.ModelCrmTask) => {
      if (typeof target === 'number') {
        const taskId = target;
        setCurrentTaskId(taskId);
      } else {
        setCurrentTask(target);
        setCurrentTaskId(target.Id);
      }
    },
    []
  );

  const fetchDataForChatHandler = useCallback(() => {
    onSuccessRef.current();
  }, []);

  const skipTask = useCallback(() => {
    setCurrentTask(null);
    setCurrentTaskId(null);
  }, []);

  const [selectedEmployee, setSelectedEmployee] = useState<BaseOptionType[]>(
    []
  );
  const {
    rightToDeleteTask,
    rightToEditTask,
    rightToEditAllTask,
    rightToCreateTask,
  } = useTasksAccess();

  const {
    openChangeEmployeeModal,
    closeChangeEmployeeModal,
    isChangeEmployeeModalOpen,
  } = useChangeEmployeeModal();

  const {
    isHistoryModalOpen,
    historyModalTitle,
    openHistoryModal,
    closeHistoryModal,
  } = useHistoryModal();
  const {
    isTaskModalOpen,
    taskModalId,
    taskModalSeed,
    closeTaskModal,
    openPreviewTaskModal,
    openEditTaskModal,
    openCreateTaskModal,
    createTaskForRange,
    createTaskForLead,
  } = useTaskModal({ selectTaskForAction, selectedEmployee });

  const {
    isOpenDeleteTasksModal,
    openDeleteTasksModal,
    closeDeleteTaskModal,
    deleteModalTitle,
    deleteModalSubtitle,
    setIsOpenDeleteTasksModal,
    target: deleteTaskTarget,
  } = useDeleteTasksModal();

  const {
    isFinishTaskModalOpen,
    openFinishTaskModal,
    closeFinishTaskModal,
    finishTasks,
    isCompletingTaskLoading,
  } = useFinishTaskModal();

  const {
    activeTab,
    onChangeTab,
    headerTitle,
    toPrevDate,
    toNextDate,
    currentDate,
  } = useTaskListRouting();

  const {
    tableHeaderForm,
    handleReset,
    selectedStatuses,
    setSelectedStatuses,
    selectedCreators,
    setSelectedCreators,
    selectedBranches,
    setSelectedBranches,
    searchTasks,
    selectorFilterData,
    selectFilterGroup,
    periodTypeFilterList,
    statusesFilterList,
    ownersFilterList,
    creatorsFilterList,
    fetchTaskList,
    refetchTaskFilters,
    taskList,
    isRegistryTableLoading,
    tableData,
    rowSelection,
    pagination,
    MassActionButton,
    selectedRowKeys,
    skipSelectedRows,
    branchesFilterList,
    taskTypesFilterList,
    selectedTaskTypes,
    setSelectedTaskTypes,
  } = useTaskRegistry({
    selectedEmployee,
    setSelectedEmployee,
    rightToDeleteTask,
    rightToEditTask,
    rightToEditAllTask,
    rightToCreateTask,
    openDeleteTasksModal,
    openFinishTaskModal,
    openChangeEmployeeModal,
    openEditTaskModal,
    selectTaskForAction,
    openPreviewTaskModal,
    activeTab,
  });

  const {
    formattedTasks,
    isCalendarsTabsLoading,
    monthWeeks,
    onMouseEnter,
    hoveredMonthTaskId,
    onChangeHoveredMonthTaskId,
    movedMonthTaskInfo,
    onChangeMovedMonthTaskInfo,
    fetchTasksForCalendars,
    taskListForCalendars,
  } = useTasksCalendar({ selectedEmployee, fetchDataForChatHandler });

  const refetchTasks = useCallback(() => {
    const defaultRefetch =
      isTaskList && activeTab !== 'registry'
        ? fetchTasksForCalendars
        : isTaskList
        ? fetchTaskList
        : emptyFn;

    defaultRefetch();
  }, [activeTab, fetchTaskList, fetchTasksForCalendars, isTaskList]);

  // Refresh both external consumers (registered via onChangeSuccess) and the
  // provider's own registry/calendar data after a task modal action. A newly
  // created task can introduce an owner/creator/branch/type combination the
  // registry's filter dropdowns don't have yet, so also refetch filter data
  // when the action was a creation (GetCrmTaskFilterData isn't tag-invalidated
  // by CreateCrmTask).
  const handleTaskModalSuccess = useCallback(
    (wasCreated?: boolean) => {
      fetchDataForChatHandler();
      refetchTasks();
      if (wasCreated) refetchTaskFilters();
    },
    [fetchDataForChatHandler, refetchTasks, refetchTaskFilters]
  );

  const deleteTaskHandler = useCallback(
    async (id: number | null) => {
      if (!id)
        return showNotification(
          'Идентификатор задачи не найден',
          FRONT_WARNING
        );

      try {
        await deleteTask({ id, ActiveBranchId }).unwrap();
        showNotification('Данные успешно удалены', SUCCESS);

        fetchDataForChatHandler();
        skipTask();
        refetchTasks();

        closeChangeEmployeeModal();
        closeTaskModal();
        closeHistoryModal();
      } catch (error) {
        showNotification(error);
      } finally {
        closeDeleteTaskModal();
      }
    },
    [
      ActiveBranchId,
      closeChangeEmployeeModal,
      closeDeleteTaskModal,
      closeTaskModal,
      closeHistoryModal,
      deleteTask,
      refetchTasks,
      showNotification,
      skipTask,
      fetchDataForChatHandler,
    ]
  );

  const handleDeleteTasksList = useCallback(
    async (CrmTaskIds: Array<number>) => {
      if (CrmTaskIds.length === 0)
        return showNotification('Список задач пуст', FRONT_WARNING);

      try {
        await deleteTaskList({ CrmTaskIds, ActiveBranchId }).unwrap();

        showNotification('Данные успешно удалены', SUCCESS);

        skipSelectedRows();
        refetchTasks();
      } catch (error) {
        showNotification(error);
      } finally {
        closeDeleteTaskModal();
      }
    },
    [
      ActiveBranchId,
      closeDeleteTaskModal,
      deleteTaskList,
      refetchTasks,
      showNotification,
      skipSelectedRows,
    ]
  );

  const deleteTasksHandleWrapper = useCallback(() => {
    if (deleteTaskTarget === 'list') {
      const taskIdsList = selectedRowKeys.map((item) => Number(item));

      handleDeleteTasksList(taskIdsList);
    } else {
      deleteTaskHandler(currentTaskId);
    }
  }, [
    currentTaskId,
    deleteTaskHandler,
    deleteTaskTarget,
    handleDeleteTasksList,
    selectedRowKeys,
  ]);

  const finishTasksHandleWrapper = useCallback(async () => {
    const CrmTaskIds = selectedRowKeys.map((item) => Number(item));

    if (CrmTaskIds.length === 0)
      return showNotification('Список задач пуст', FRONT_WARNING);

    try {
      await finishTasks(CrmTaskIds);

      refetchTasks();
      skipSelectedRows();
    } catch (error) {
      showNotification(error);
    }
  }, [
    finishTasks,
    selectedRowKeys,
    showNotification,
    refetchTasks,
    skipSelectedRows,
  ]);

  const currentTaskList = useMemo(() => {
    return isTaskList && activeTab !== 'registry'
      ? taskListForCalendars
      : taskList;
  }, [activeTab, isTaskList, taskList, taskListForCalendars]);

  const searchTaskWithId = useCallback(
    (id: number) => {
      const task = currentTaskList.find((item) => item.Id === id);
      if (task) {
        selectTaskForAction(task);
      }
    },
    [currentTaskList, selectTaskForAction]
  );

  const getRightToDeleteTask = useCallback((): boolean => {
    const task = currentTaskList.find((item) => item.Id === currentTaskId);
    if (userId === task?.UserCreateId && rightToCreateTask) {
      return true;
    } else if (rightToDeleteTask) {
      return true;
    } else {
      return false;
    }
  }, [
    currentTaskId,
    currentTaskList,
    rightToDeleteTask,
    rightToCreateTask,
    userId,
  ]);

  const getRightToEditTask = useCallback((): boolean => {
    const task = currentTaskList.find((item) => item.Id === currentTaskId);
    const taskOwnersIds = task?.Owners?.map(({ UserId }) => UserId) || [];
    if (userId === currentTask?.UserCreateId && rightToCreateTask) {
      return true;
    } else if (rightToEditTask && userId && taskOwnersIds.includes(userId)) {
      return true;
    } else if (rightToEditAllTask) {
      return true;
    } else {
      return false;
    }
  }, [
    currentTask,
    currentTaskId,
    currentTaskList,
    rightToEditTask,
    rightToEditAllTask,
    rightToCreateTask,
    userId,
  ]);

  // Header "…" menu for the unified task modal — preview/edit collapse into the
  // modal itself, so only History and Delete remain.
  const taskModalDropdownItems = useMemo<ItemType[]>(() => {
    const items: ItemType[] = [
      getMassActionMenuItem({
        key: TASK_HISTORY_KEY,
        label: 'История изменений',
        onClick: () => {
          closeTaskModal();
          openHistoryModal();
        },
        disabled: false,
      }),
    ];

    if (getRightToDeleteTask()) {
      items.push(
        getMassActionMenuItem({
          key: DELETE_TASKS_KEY,
          label: 'Удалить задачу',
          onClick: () => {
            closeTaskModal();
            openDeleteTasksModal('task');
          },
          disabled: false,
        })
      );
    }

    return items;
  }, [
    closeTaskModal,
    getRightToDeleteTask,
    openDeleteTasksModal,
    openHistoryModal,
  ]);

  const historyModalDropdownItems = useMemo(
    () =>
      ModalDropdownItems({
        isEditModal: false,
        isHistoryModal: true,
        isPreviewModal: false,
        handleDeleteModalOpen: () => {
          closeHistoryModal();
          openDeleteTasksModal('task');
        },
        openHistoryModal,
        currentTaskId: currentTaskId || 0,

        handleEditModalOpen: () => {
          if (!currentTask)
            return showNotification(
              'Ошибка при открытии модального окна редактирования задачи',
              FRONT_WARNING
            );

          closeHistoryModal();
          openEditTaskModal(currentTask);
        },
        handlePreviewModalOpen: () => {
          closeHistoryModal();
          openPreviewTaskModal(currentTaskId || 0);
        },
        rightToCreateDeleteTask: getRightToDeleteTask(),
        rightToEditTask: getRightToEditTask(),
      }),
    [
      closeHistoryModal,
      currentTask,
      currentTaskId,
      getRightToDeleteTask,
      getRightToEditTask,
      openDeleteTasksModal,
      openEditTaskModal,
      openPreviewTaskModal,
      openHistoryModal,
      showNotification,
    ]
  );

  useEffect(() => {
    if (currentTaskId) {
      searchTaskWithId(currentTaskId);
    }
  }, [currentTaskId, searchTaskWithId]);

  return (
    <Provider
      value={{
        ...formattedTasks,
        activeTab,
        onChangeTab,
        headerTitle,
        toPrevDate,
        toNextDate,
        currentDate,
        isCalendarsTabsLoading,
        monthWeeks,
        onMouseEnter,
        hoveredMonthTaskId,
        onChangeHoveredMonthTaskId,
        movedMonthTaskInfo,
        onChangeMovedMonthTaskInfo,
        openCreateTaskModal,
        closeTaskDetailsModal: closeTaskModal,
        createTaskForRange,
        openEditTaskModal,
        tableHeaderForm,
        handleReset,
        selectedStatuses,
        setSelectedStatuses,
        selectedCreators,
        setSelectedCreators,
        selectedBranches,
        setSelectedBranches,
        selectedEmployee,
        setSelectedEmployee,
        searchTasks,
        selectorFilterData,
        selectFilterGroup,
        periodTypeFilterList,
        statusesFilterList,
        ownersFilterList,
        creatorsFilterList,
        isRegistryTableLoading,
        tableData,
        rowSelection,
        pagination,
        MassActionButton,
        openPreviewTaskModal,
        createTaskForLead,
        selectTaskForAction,
        onChangeSuccess,
        isChangeEmployeeModalOpen,
        closeChangeEmployeeModal,
        selectedRowKeys,
        skipSelectedRows,
        branchesFilterList,
        taskTypesFilterList,
        selectedTaskTypes,
        setSelectedTaskTypes,
      }}
    >
      {children}
      <Form form={tableHeaderForm} component={false} />
      <TaskModal
        isOpen={isTaskModalOpen}
        taskId={taskModalId}
        seed={taskModalSeed}
        menuItems={taskModalId !== null ? taskModalDropdownItems : undefined}
        onClose={closeTaskModal}
        onSuccess={handleTaskModalSuccess}
      />
      <ConfirmDeletingElement
        modalOpen={isOpenDeleteTasksModal}
        setModalOpen={setIsOpenDeleteTasksModal}
        handleDelete={deleteTasksHandleWrapper}
        content={deleteModalSubtitle}
        title={deleteModalTitle}
      />
      <FinishTaskModal
        isOpen={isFinishTaskModalOpen}
        isLoading={isCompletingTaskLoading}
        onCancel={closeFinishTaskModal}
        onSave={finishTasksHandleWrapper}
      />
      <HistoryModal
        isModalOpen={isHistoryModalOpen}
        historyModalTitle={historyModalTitle}
        handleCancelHistoryModal={closeHistoryModal}
        currentTaskId={currentTaskId}
        dropdownMenu={historyModalDropdownItems}
      />
    </Provider>
  );
};

export const useTaskListContext = () => {
  const context = useContext(TaskListProviderContext);
  if (!context) {
    throw new Error('usTaskListContext must be used within a TaskListProvider');
  }
  return context;
};
